import { Badge } from "@mantine/core";
import type { FC } from "react";
import { clientStatuses } from "../../../constants";
import {
  UIStatuses,
  icons as statusIcons,
} from "../../../pages/TaxProClientList/components/OverviewStatuses/StatusConstants";

interface StatusBadgeProps {
  status: string;
  /** Optional size override, defaults to "lg" */
  size?: string;
  /** Optional icon size override, defaults to 20 */
  iconSize?: number;
  /** Optional onClick handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Optional className for additional styling */
  className?: string;
}

/**
 * Color mapping that follows clientStatuses exactly as defined in ClientStatus.scss
 * Maps status class to both background and text colors
 * Note: The class names don't always match the background colors (e.g., Ready To Go)
 */
const statusColorMap: Record<
  string,
  { backgroundColor: string; textColor: string }
> = {
  D6F0FF: { backgroundColor: "#D6F0FF", textColor: "#0077cc" }, // Quote Sent
  "6CBAF2": { backgroundColor: "#6CBAF2", textColor: "#00477a" }, // In Progress
  D7F7DC: { backgroundColor: "#D7F7DC", textColor: "#176e26" }, // Completed - light green
  CECDF8: { backgroundColor: "#CECDF8", textColor: "#383794" }, // Ready To E-File
  F3E1A4: { backgroundColor: "#F3E1A4", textColor: "#a78a2e" }, // Waiting
  FDD3D0: { backgroundColor: "#FDD3D0", textColor: "#dc373a" }, // Signatures Needed
  D9D9D9: { backgroundColor: "#D9D9D9", textColor: "#444444" }, // Archived
  F9C599: { backgroundColor: "#F9C599", textColor: "#c15800" }, // New
  "5f7f64": { backgroundColor: "#0077cc", textColor: "#fff" }, // Ready To Go - blue (SCSS overrides with #0077cc)
};

/**
 * Get colors from clientStatuses using the exact color mapping
 */
const getStatusColors = (
  status: string
): { backgroundColor: string; textColor: string } => {
  const statusEntry = Object.values(clientStatuses).find(
    (item) => item.status === status
  );

  if (statusEntry && statusColorMap[statusEntry.class]) {
    return statusColorMap[statusEntry.class];
  }

  // Default colors for unknown status
  return { backgroundColor: "#6B7280", textColor: "#ffffff" };
};

/**
 * Common StatusBadge component that displays client status with consistent styling
 * Uses Mantine Badge component with icons from StatusConstants and colors from clientStatuses
 * :param status: The status string to display
 * :param size: Optional size for the badge (default: "lg")
 * :param iconSize: Optional icon size (default: 20)
 * :param onClick: Optional click handler
 * :param className: Optional additional CSS classes
 * :return: Mantine Badge with status styling and icon
 */
export const StatusBadge: FC<StatusBadgeProps> = ({
  status,
  size = "lg",
  iconSize = 20,
  onClick,
  className,
}) => {
  // Get icon from UIStatuses
  const statusIndex = UIStatuses.findIndex((s) => s.text === status);
  const icon = statusIndex >= 0 ? statusIcons[statusIndex] : null;

  // Get colors from clientStatuses with exact color mapping
  const { backgroundColor, textColor } = getStatusColors(status);

  // Clone the icon element with the custom size if icon exists
  const iconWithSize = icon
    ? typeof icon === "object" && "props" in icon
      ? {
          ...icon,
          props: {
            ...icon.props,
            size: iconSize,
          },
        }
      : icon
    : null;

  return (
    <Badge
      color={backgroundColor}
      variant="filled"
      size={size}
      radius="xl"
      leftSection={iconWithSize}
      onClick={onClick}
      className={className}
      styles={{
        root: {
          backgroundColor: backgroundColor,
          color: textColor,
          fontWeight: 600,
          textTransform: "none",
          paddingLeft: "12px",
          paddingRight: "16px",
          paddingTop: "8px",
          paddingBottom: "8px",
          fontSize: "14px",
          height: "auto",
          cursor: onClick ? "pointer" : "default",
        },
        section: {
          marginRight: "6px",
        },
      }}
    >
      {status || "New"}
    </Badge>
  );
};
